/**
 * Browser desktop notifications (when tab is in background or site not focused).
 * Use for real-time Echo events so users get notified even when the tab is not visible.
 */

const DEFAULT_TAG = 'complyone-notification'

/**
 * @returns {boolean} Whether the Notification API is available.
 */
export function isDesktopNotificationSupported() {
  return typeof window !== 'undefined' && 'Notification' in window
}

/**
 * @returns {'granted'|'denied'|'default'} Current permission state.
 */
export function getDesktopNotificationPermission() {
  if (!isDesktopNotificationSupported()) return 'denied'
  return Notification.permission
}

/**
 * Request permission for desktop notifications. Call when appropriate (e.g. after login or before first notification).
 * @returns {Promise<boolean>} True if permission granted, false otherwise.
 */
export async function requestDesktopNotificationPermission() {
  if (!isDesktopNotificationSupported()) return false
  if (Notification.permission === 'granted') return true
  if (Notification.permission === 'denied') return false
  const permission = await Notification.requestPermission()
  return permission === 'granted'
}

/**
 * Show a desktop notification. Does nothing if permission not granted or unsupported.
 * @param {Object} options
 * @param {string} options.title - Notification title
 * @param {string} [options.body] - Optional body text
 * @param {string} [options.tag] - Optional tag (replaces previous with same tag to avoid stacking)
 * @param {string} [options.icon] - Optional icon URL
 * @param {() => void} [options.onClick] - Optional callback when user clicks the notification (e.g. focus window, navigate)
 */
export function showDesktopNotification({ title, body = '', tag = DEFAULT_TAG, icon, onClick }) {
  if (!isDesktopNotificationSupported() || Notification.permission !== 'granted') return

  const notification = new Notification(title, {
    body: body || undefined,
    tag,
    icon: icon || undefined,
  })

  notification.onclick = () => {
    window.focus()
    notification.close()
    if (typeof onClick === 'function') onClick()
  }
}
